'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Anchor, Box, Button, Space, Stack, Text, Title } from '@mantine/core'
import { usePrevious } from '@mantine/hooks'
import { SignedInNotice } from '@phoenix-web/react-shared/rsc-components'
import { Audience } from '@phoenix-web/utils/journey'
import { authApiPaths, HOMEBUYER_SIGNUP_URL, LOGOUT_WITH_REDIRECT_TO_TERRACE_SIGNUP } from '@/constants'
import { authSignupContent } from '@/text'
import AccountSignupForm from './AccountSignUpForm'

const { LOGIN_TEXT, LOGIN_LINK_LABEL, TITLE, TITLE_AUDIENCE_PICKER } = authSignupContent

/** Shared by the signup form box and the notice above it so their edges line up. */
const FORM_BOX_WIDTH = { maw: '350px', w: { base: '90%', md: '60%' } } as const
/** The picker box is content-sized, so the notice takes a fixed width. Matches courtyard2. */
const AUDIENCE_NOTICE_WIDTH = { maw: 420, w: '100%' } as const

export default function AuthSignupContent({
  signedIn,
  signedInEmail,
}: {
  /** Keyed off `sub`, not email: a profile can have a session and no email. */
  signedIn?: boolean
  signedInEmail?: string
}) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  // With app router, these values should exist on first render
  const returnTo = searchParams?.get('returnTo')
  const returnToSearchQuery = returnTo ? `returnTo=${encodeURIComponent(returnTo)}` : ''
  const audience = searchParams?.get('audience')

  const isPartner = [Audience.AGENT, Audience.LENDER, 'partner'].includes(audience || '')
  const [hideSignup, setHideSignup] = useState(!isPartner)

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams ? searchParams.toString() : '')
      params.set(name, value)

      return params.toString()
    },
    [searchParams]
  )

  const previousAudienceValue = usePrevious(audience)
  useEffect(() => {
    if (previousAudienceValue !== undefined && previousAudienceValue !== audience) {
      setHideSignup(!audience)
    }
  }, [previousAudienceValue, audience])

  const handlePartnerSelection = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setHideSignup(false)
    const target = e.currentTarget
    router.push(`${pathname}?${createQueryString('audience', target.value)}`)
  }

  const showAudienceSelector = !isPartner && hideSignup

  return (
    <>
      {/* The notice renders above the title, outside either branch's own box, so
          it has to carry that box's width itself — courtyard2 gets this for free
          by nesting inside its form box. Spreading the same object the form uses
          keeps the two edges aligned at every breakpoint; a fixed width here
          would drift once the column is narrow enough that 60% beats the 350 cap. */}
      {signedIn && (
        <Box m="auto" {...(showAudienceSelector ? AUDIENCE_NOTICE_WIDTH : FORM_BOX_WIDTH)}>
          <SignedInNotice email={signedInEmail} logoutHref={LOGOUT_WITH_REDIRECT_TO_TERRACE_SIGNUP} />
        </Box>
      )}
      <Text style={{ fontSize: '18px', fontWeight: 'bold' }}>{TITLE}</Text>
      {showAudienceSelector ? (
        // Audience Selector
        <Box>
          <Title order={3} ta="center">
            {TITLE_AUDIENCE_PICKER}
          </Title>
          <Space h="lg" />
          <Stack gap="sm">
            <Button
              component="a"
              type="button"
              variant="outline"
              color="dark.9"
              radius="var(--mantine-radius-sm)"
              href={HOMEBUYER_SIGNUP_URL}
            >
              I&rsquo;m a homebuyer
            </Button>
            <Button
              type="button"
              variant="outline"
              color="dark.9"
              radius="var(--mantine-radius-sm)"
              value={Audience.AGENT}
              onClick={(e) => handlePartnerSelection(e)}
            >
              I&rsquo;m an agent
            </Button>
            <Button
              type="button"
              variant="outline"
              color="dark.9"
              radius="var(--mantine-radius-sm)"
              value={Audience.LENDER}
              onClick={(e) => handlePartnerSelection(e)}
            >
              I&rsquo;m a lender
            </Button>
          </Stack>
          <Space h="xxl" />
        </Box>
      ) : (
        // Partner Signup Form
        <Box {...FORM_BOX_WIDTH}>
          <Space h="xs" />
          <AccountSignupForm />
          <Space h="lg" />
          {/* "Already have an account? Log in" is noise for someone who is
              already in one — the notice above offers the action they need.
              It also carries the gap under Cancel, so replace that height
              rather than letting the button sit against the page edge. */}
          {signedIn ? (
            <Space h="xl" />
          ) : (
            <Text size="sm">
              {LOGIN_TEXT}{' '}
              <Anchor fs="none" href={`${authApiPaths.LOGIN}?${returnToSearchQuery}`}>
                {LOGIN_LINK_LABEL}
              </Anchor>
            </Text>
          )}
        </Box>
      )}
    </>
  )
}
